<template>
  <Menu
    as="div"
    v-slot="{ close }"
    class="relative language-menu mx-[8px] md:mx-[10px] lg:mx-[12px] ltr:first:ml-0 ltr:last:mr-0 rtl:first:mr-0 rtl:last:ml-0"
  >
    <MenuButton
      class="leading-none pr-[12px] inline-block transition-all relative top-[2px] hover:text-primary-500"
    >
      <i class="material-symbols-outlined !text-[20px] md:!text-[22px]">
        translate
      </i>
      <i
        class="ri-arrow-down-s-line text-[15px] absolute -right-[3px] top-1/2 -translate-y-1/2 -mt-[2px]"
      ></i>
    </MenuButton>

    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <MenuItems
        class="bg-white dark:bg-[#0c1427] transition-all shadow-3xl dark:shadow-none pt-[13px] md:pt-[14px] absolute mt-[18px] md:mt-[21px] w-[200px] md:w-[240px] z-[1] top-full ltr:left-0 ltr:md:left-auto ltr:lg:right-0 rtl:right-0 rtl:md:right-auto rtl:lg:left-0 rounded-md focus:outline-hidden"
      >
        <span
          class="block text-black dark:text-white font-semibold px-[20px] pb-[14px] text-sm md:text-[15px]"
        >
          {{ directT('common.chooseLanguage') }}
        </span>
        <ul>
          <li
            class="border-t border-dashed border-gray-100 dark:border-[#172036]"
          >
            <button
              type="button"
              class="text-black dark:text-white px-[20px] py-[12px] d-block w-full font-medium"
              @click="changeLanguage('en'); close()"
              :class="{ 'bg-gray-50 dark:bg-[#172036]': currentLanguage === 'en' }"
            >
              <div class="flex items-center">
                <img
                  src="@/assets/images/flags/usa.svg"
                  class="w-[30px] ltr:mr-[10px] rtl:ml-[10px]"
                  alt="usa"
                />
                English
                <span v-if="currentLanguage === 'en'" class="ml-2 text-green-500">✓</span>
              </div>
            </button>
          </li>
          <li
            class="border-t border-dashed border-gray-100 dark:border-[#172036]"
          >
            <button
              type="button"
              class="text-black dark:text-white px-[20px] py-[12px] d-block w-full font-medium"
              @click="changeLanguage('he'); close()"
              :class="{ 'bg-gray-50 dark:bg-[#172036]': currentLanguage === 'he' }"
            >
              <div class="flex items-center">
                <img
                  src="@/assets/images/flags/israel.svg"
                  class="w-[30px] ltr:mr-[10px] rtl:ml-[10px]"
                  alt="israel"
                />
                Hebrew (עברית)
                <span v-if="currentLanguage === 'he'" class="ml-2 text-green-500">✓</span>
              </div>
            </button>
          </li>
          <li
            class="border-t border-dashed border-gray-100 dark:border-[#172036]"
          >
            <button
              type="button"
              class="text-black dark:text-white px-[20px] py-[12px] d-block w-full font-medium"
              @click="changeLanguage('ar'); close()"
              :class="{ 'bg-gray-50 dark:bg-[#172036]': currentLanguage === 'ar' }"
            >
              <div class="flex items-center">
                <img
                  src="@/assets/images/flags/brazil.svg"
                  class="w-[30px] ltr:mr-[10px] rtl:ml-[10px]"
                  alt="arabic"
                />
                Arabic (العربية)
                <span v-if="currentLanguage === 'ar'" class="ml-2 text-green-500">✓</span>
              </div>
            </button>
          </li>
        </ul>
      </MenuItems>
    </transition>
  </Menu>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed } from "vue";
import { Menu, MenuButton, MenuItems } from "@headlessui/vue";
import { setLanguage, reloadLanguage, type SupportedLanguage } from "@/i18n";
import { useI18n } from "vue-i18n";
import emitter from "@/utils/emitter";

// Direct import of translation files for manual translation lookup
import enTranslations from '@/i18n/locales/en.json';
import heTranslations from '@/i18n/locales/he.json';
import arTranslations from '@/i18n/locales/ar.json';

// Create type for translation object structure
type TranslationObject = Record<string, any>;

export default defineComponent({
  name: "LanguageMenu",
  components: {
    Menu,
    MenuButton,
    MenuItems,
  },
  setup() {
    const { locale, t } = useI18n();
    const currentLanguage = computed(() => locale.value as SupportedLanguage);
    const translationDebug = ref(false);

    // Store direct references to translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string) => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return enResult;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      return result;
    };

    const changeLanguage = (lang: SupportedLanguage) => {
      console.log(`Language menu: changing language to ${lang}`);
      
      // Force reload if selecting the same language to refresh translations
      if (locale.value === lang) {
        console.log('Same language selected, forcing refresh');
        
        // מחיקת מצב האקורדיונים המוקדם
        localStorage.removeItem('trezo-open-sidebar-menus');
        
        // אתחול מחדש של התפריטים בסיידבר אחרי ריענון קל של הדום
        setTimeout(() => {
          document.querySelector('.sidebar-area')?.classList.add('force-refresh');
          setTimeout(() => {
            document.querySelector('.sidebar-area')?.classList.remove('force-refresh');
            // כפה אירוע שינוי שפה למרות שלא שינינו שפה בפועל
            emitter.emit('language:changed', lang);
          }, 50);
        }, 50);
        
        return;
      }
      
      setLanguage(lang);
      
      // Test if translation works after change
      console.log(`Translation test after change: ${directT('sidebar.main')}`);
    };

    const reloadLanguage = () => {
      console.log('Reloading language');
      document.location.reload();
    };

    onMounted(() => {
      // Apply initial language/direction from localStorage if available
      const savedLang = localStorage.getItem('language');
      if (savedLang && ['en', 'he', 'ar'].includes(savedLang)) {
        if (locale.value !== savedLang) {
          console.log(`Language menu: initializing language to ${savedLang} from localStorage`);
          setLanguage(savedLang as SupportedLanguage);
        }
      }
      
      // Test if translations work
      console.log(`Initial translation test: ${directT('sidebar.main')}`);
    });

    return {
      currentLanguage,
      changeLanguage,
      translationDebug,
      reloadLanguage,
      directT
    };
  },
});
</script>
